/**
 * SISTEMA DE ESTABILIDAD - LIVEWIFI CENTINELA
 * 
 * Este hook proporciona:
 * - Health check automático del backend
 * - Reconexión automática cuando se pierde conexión
 * - Reintentos con backoff exponencial
 * - Estado de conexión global
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configuración de estabilidad
const CONFIG = {
  HEALTH_CHECK_INTERVAL: 15000,      // Cada 15 segundos
  DATA_REFRESH_INTERVAL: 10000,      // Refrescar datos cada 10 segundos
  MAX_RETRIES: 5,                    // Máximo reintentos
  INITIAL_RETRY_DELAY: 1000,         // 1 segundo inicial
  MAX_RETRY_DELAY: 30000,            // Máximo 30 segundos
  CONNECTION_TIMEOUT: 10000,         // 10 segundos timeout
};

export const ConnectionStatus = {
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
  RECONNECTING: 'reconnecting',
  ERROR: 'error'
};

export function useStableConnection(onDataRefresh, enabled = true) {
  const [connectionStatus, setConnectionStatus] = useState(ConnectionStatus.CONNECTED);
  const [lastSuccessfulFetch, setLastSuccessfulFetch] = useState(Date.now());
  const [retryCount, setRetryCount] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const retryTimeoutRef = useRef(null);
  const healthCheckRef = useRef(null);
  const dataRefreshRef = useRef(null);

  // Calcular delay con backoff exponencial
  const getRetryDelay = useCallback((retry) => {
    const delay = Math.min(
      CONFIG.INITIAL_RETRY_DELAY * Math.pow(2, retry),
      CONFIG.MAX_RETRY_DELAY
    );
    return delay;
  }, []);

  // Health check del backend
  const performHealthCheck = useCallback(async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), CONFIG.CONNECTION_TIMEOUT);
      
      const response = await axios.get(`${API}/health`, {
        signal: controller.signal,
        timeout: CONFIG.CONNECTION_TIMEOUT
      });
      
      clearTimeout(timeoutId);
      
      // Check both status and ffmpeg availability
      const isHealthy = response.data?.status === 'healthy' || response.data?.status === 'degraded';
      const ffmpegOk = response.data?.ffmpeg === 'available' || response.data?.ffmpeg === 'reinstalled';
      
      if (isHealthy) {
        if (connectionStatus !== ConnectionStatus.CONNECTED) {
          setConnectionStatus(ConnectionStatus.CONNECTED);
          setRetryCount(0);
          console.log('[CENTINELA] Conexión restaurada');
        }
        setLastSuccessfulFetch(Date.now());
        
        // Log FFmpeg status if there was an issue
        if (!ffmpegOk) {
          console.warn('[CENTINELA] ⚠️ FFmpeg no disponible - las cámaras pueden no funcionar');
        }
        
        return { healthy: true, ffmpegOk };
      }
      return { healthy: false, ffmpegOk: false };
    } catch (error) {
      console.warn('[CENTINELA] Health check falló:', error.message);
      return { healthy: false, ffmpegOk: false };
    }
  }, [connectionStatus]);

  // Refrescar datos con reintentos
  const refreshDataWithRetry = useCallback(async () => {
    if (!enabled || isRefreshing) return;
    
    setIsRefreshing(true);
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), CONFIG.CONNECTION_TIMEOUT);
      
      await onDataRefresh();
      
      clearTimeout(timeoutId);
      setLastSuccessfulFetch(Date.now());
      setConnectionStatus(ConnectionStatus.CONNECTED);
      setRetryCount(0);
      
    } catch (error) {
      console.warn('[CENTINELA] Error refrescando datos:', error.message);
      
      if (retryCount < CONFIG.MAX_RETRIES) {
        setConnectionStatus(ConnectionStatus.RECONNECTING);
        const delay = getRetryDelay(retryCount);
        
        console.log(`[CENTINELA] Reintentando en ${delay/1000}s (intento ${retryCount + 1}/${CONFIG.MAX_RETRIES})`);
        
        retryTimeoutRef.current = setTimeout(() => {
          setRetryCount(prev => prev + 1);
          refreshDataWithRetry();
        }, delay);
      } else {
        setConnectionStatus(ConnectionStatus.ERROR);
        console.error('[CENTINELA] Se agotaron los reintentos');
      }
    } finally {
      setIsRefreshing(false);
    }
  }, [enabled, isRefreshing, onDataRefresh, retryCount, getRetryDelay]);

  // Forzar reconexión manual
  const forceReconnect = useCallback(() => {
    setRetryCount(0);
    setConnectionStatus(ConnectionStatus.RECONNECTING);
    refreshDataWithRetry();
  }, [refreshDataWithRetry]);

  // Iniciar health checks periódicos
  useEffect(() => {
    if (!enabled) return;

    // Health check inicial
    performHealthCheck();

    // Health check periódico
    healthCheckRef.current = setInterval(() => {
      performHealthCheck();
    }, CONFIG.HEALTH_CHECK_INTERVAL);

    return () => {
      if (healthCheckRef.current) {
        clearInterval(healthCheckRef.current);
      }
    };
  }, [enabled, performHealthCheck]);

  // Refresco automático de datos
  useEffect(() => {
    if (!enabled) return;

    dataRefreshRef.current = setInterval(() => {
      if (connectionStatus === ConnectionStatus.CONNECTED) {
        refreshDataWithRetry();
      }
    }, CONFIG.DATA_REFRESH_INTERVAL);

    return () => {
      if (dataRefreshRef.current) {
        clearInterval(dataRefreshRef.current);
      }
    };
  }, [enabled, connectionStatus, refreshDataWithRetry]);

  // Limpiar timeouts al desmontar
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  // Detectar cuando el navegador vuelve a estar online
  useEffect(() => {
    const handleOnline = () => {
      console.log('[CENTINELA] Conexión de red restaurada');
      forceReconnect();
    };

    const handleOffline = () => {
      console.log('[CENTINELA] Conexión de red perdida');
      setConnectionStatus(ConnectionStatus.DISCONNECTED);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [forceReconnect]);

  return {
    connectionStatus,
    lastSuccessfulFetch,
    retryCount,
    isRefreshing,
    forceReconnect,
    isConnected: connectionStatus === ConnectionStatus.CONNECTED,
    isReconnecting: connectionStatus === ConnectionStatus.RECONNECTING,
    hasError: connectionStatus === ConnectionStatus.ERROR
  };
}

export default useStableConnection;
